import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';

import Icon from 'components/commons/icon/Icon';
import OKCanelModal from 'components/features/modal/OKCancelModal';
import PremiumModal from 'components/features/modal/PremiumModal';
import Loading from 'components/commons/loading/Loading';

import * as api from 'config/api';
import * as env from 'config/environment';
import * as consts from 'config/consts';

import { useRouting } from 'hooks/useRouting';
import { useEffectMounted } from 'hooks/useEffectMounted';
import { useModal } from 'hooks/useModal';
import { useScroll } from 'hooks/useScroll';
import {
  getScrollStorage,
  getRoutingStorage,
  updateScrollStorage,
  Scroll,
} from 'utils/storage';
import { InformationFromMoneytreeModal } from 'components/features/modal/InformationFromMoneytreeModal';
import { AlignmentAccounLimitModal } from 'components/features/modal/AlignmentAccounLimitModal';
import { AccountRegisteringModal } from 'components/features/modal/AccountRegisteringModal';
import {
  isPremium as isPremiumRaw,
  isApp,
  isIosApp,
} from 'components/features/utils/userStatus';
import { DataLinkWithdrawalAccountModal } from 'components/features/modal/DataLinkWithdrawalAccountModal';
import { WaitUntilNextUpdateModal } from 'components/features/modal/WaitUntilNextUpdateModalModal';
import { linkToOptionalAssetRegistration } from './OptionalAssetRegistration';

const useIsPremium = () => {
  const isDebug = false;
  const [isPremiumState, setIsPremium] = useState(false);
  const isPremium = isDebug
    ? () => {
        return isPremiumState;
      }
    : isPremiumRaw;
  const Element = useMemo(() => {
    if (!isDebug) return () => null;
    return () => (
      <button onClick={() => setIsPremium(prev => !prev)}>Premium変換</button>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPremiumState]);
  return {
    isPremium,
    Element,
  };
};

// TODO: 外部かAPIに逃したい
const securityCompanies = [
  {
    brokerID: consts.MONOBrokerIds.SBI証券,
    name: 'SBI証券',
    url:
      'https://minkabu.jp/hikaku/redirect_tw/sbi.html?utm_source=tw&utm_medium=securities_list&utm_campaign=',
    isIosShow: true,
    isRegistrationPossible: true,
  },
  {
    brokerID: consts.MONOBrokerIds.楽天証券,
    name: '楽天証券',
    url:
      'https://minkabu.jp/hikaku/redirect_tw/rakuten.html?utm_source=tw&utm_medium=securities_list&utm_campaign=',
    isIosShow: false,
    isRegistrationPossible: true,
  },
  // {
  //   brokerID: consts.MONOBrokerIds.松井証券,
  //   name: '松井証券',
  //   url:
  //     'https://minkabu.jp/hikaku/redirect_tw/matsui.html?utm_source=tw&utm_medium=securities_list&utm_campaign=',
  //   isIosShow: false,
  //   isRegistrationPossible: false,
  // },
  // {
  //   brokerID: consts.MONOBrokerIds.三菱UFJeスマート証券,
  //   name: '三菱UFJ eスマート証券',
  //   url:
  //     'https://minkabu.jp/hikaku/redirect_tw/kabucom.html?utm_source=tw&utm_medium=securities_list&utm_campaign=',
  //   isIosShow: false,
  //   isRegistrationPossible: false,
  // },
  // TODO: MINKABU ROBOの導線をコメントアウト
  // {
  //   brokerID: consts.BrokerIDs.みんかぶロボ,
  //   name: 'MINKABU ROBO',
  //   url: '',
  // },
  // {
  //   brokerID: consts.MONOBrokerIds.マネックス証券,
  //   name: 'マネックス証券',
  //   url: '',
  //   isIosShow: false,
  //   isRegistrationPossible: false,
  // },
  // {
  //   brokerID: consts.MONOBrokerIds.野村證券,
  //   name: '野村證券',
  //   url: '',
  //   isIosShow: false,
  //   isRegistrationPossible: false,
  // },
  // {
  //   brokerID: consts.MONOBrokerIds.大和証券,
  //   name: '大和証券',
  //   url: '',
  //   isIosShow: false,
  //   isRegistrationPossible: false,
  // },
  // {
  //   brokerID: consts.MONOBrokerIds.SMBC日興証券,
  //   name: 'SMBC日興証券',
  //   url: '',
  //   isIosShow: false,
  //   isRegistrationPossible: false,
  // },
  // {
  //   brokerID: consts.MONOBrokerIds.岡三証券,
  //   name: '岡三証券',
  //   url: '',
  //   isIosShow: false,
  //   isRegistrationPossible: false,
  // },
  // {
  //   brokerID: consts.MONOBrokerIds.みずほ証券,
  //   name: 'みずほ証券',
  //   url: '',
  //   isIosShow: false,
  //   isRegistrationPossible: false,
  // },
  // {
  //   brokerID: consts.MONOBrokerIds.三菱UFJモルガンスタンレー証券,
  //   name: (
  //     <>
  //       三菱UFJモルガン・
  //       <br />
  //       スタンレー証券
  //     </>
  //   ),
  //   url: '',
  //   isIosShow: false,
  //   isRegistrationPossible: false,
  // },
];

const definedContributionPlanCompanies = [
  {
    brokerID: consts.MONOBrokerIds['JIS&T'],
    name: 'JIS&T',
    url: '',
  },
  {
    brokerID: consts.MONOBrokerIds.SBIベネフィットシステムズ,
    name: (
      <>
        SBIベネフィット
        <br />
        システムズ
      </>
    ),
    url: '',
  },
  {
    brokerID: consts.MONOBrokerIds.NRK,
    name: 'NRK',
    url: '',
  },
];

const SUPPORT_LINK =
  'https://support.minkabu.jp/hc/ja/articles/360059043194-%E5%AF%BE%E5%BF%9C%E8%A8%BC%E5%88%B8%E5%8F%A3%E5%BA%A7-%E5%AF%BE%E5%BF%9C%E9%87%91%E8%9E%8D%E5%95%86%E5%93%81';

const doRegister = (
  brokerID: string,
  res: api.PrepareUserBrokerResult
): void => {
  window.location.href = `${env.monoAccountURL}/register-external-integration-sec-account/${brokerID}?onetimeToken=${res.OnetimeToken}&provisionalId=${res.ProvisionalID}&callbackUrl=${env.appURL}/userbrokerlink&returnUri=${env.appURL}/securityaccounts&completeUrl=${env.appURL}/securityaccounts`;
};

const SecurityAccountsLinkInternal = ({
  userBroker,
}: {
  userBroker: api.GetUserBrokerResultItem[];
}) => {
  const routing = useRouting();
  const { Element, isPremium } = useIsPremium();
  const firstRoute = routing.getFirstRoute();

  const redirectURI = `${env.appURL}/moneytreelink`;
  const scopes = [
    'guest_read',
    'accounts_read',
    'transactions_read',
    'investment_accounts_read',
    'investment_transactions_read',
    'points_read',
    'life_insurance_read',
    'request_refresh',
  ];
  const state = new Date().getTime();

  const moneytreeAuthorizeURL = encodeURI(
    `${env.moneytreeBaseURL}/oauth/authorize?client_id=${
      env.moneytreeClientId
    }&redirect_uri=${redirectURI}&response_type=code&scope=${scopes.join(
      ' '
    )}&locale=ja&state=${state}:${firstRoute.path}`
  );

  const [user, setUser] = useState<api.GetUserResut>();
  const [shownUnlinkConfirmDialog, setShownUnlinkConfirmDialog] = useState(
    false
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPremium, setShowPremium] = useState<boolean>(false);
  const [
    isShowInformationFromMoneytreeModal,
    {
      hideModal: hideInformationFromMoneytreeModal,
      showModal: showInformationFromMoneytreeModal,
    },
  ] = useModal();
  const [
    isShowAlignmentAccounLimitModal,
    {
      hideModal: hideAlignmentAccounLimitModal,
      showModal: showAlignmentAccounLimitModal,
    },
  ] = useModal();
  const [
    isShowAccountRegisteringModal,
    {
      hideModal: hideAccountRegisteringModal,
      showModal: showAccountRegisteringModal,
    },
  ] = useModal();
  const [
    isShowDataLinkWithdrawalAccountModal,
    {
      hideModal: hideDataLinkWithdrawalAccountModal,
      showModal: showDataLinkWithdrawalAccountModal,
    },
  ] = useModal();
  const [
    isShowWaitUntilNextUpdateModal,
    {
      hideModal: hideWaitUntilNextUpdateModal,
      showModal: showWaitUntilNextUpdateModal,
    },
  ] = useModal();

  const unmounted = useRef<boolean>(false);

  const doUnlinkOK = (): void => {
    if (isLoading) return;
    api.unlinkMoneytree().then(ret => {
      document.location.reload();
    });
  };
  const doUnlinkCancel = (): void => {
    if (isLoading) return;
    setShownUnlinkConfirmDialog(false);
  };

  // プレミアムダイアログ非表示
  const onClosePremium = (): void => {
    if (isLoading) return;
    setShowPremium(false);
  };

  // マネーツリーへ遷移する前にルーティング操作を切り替える
  const handleMoneytreeLink = () => {
    routing.finishNormalRouting();
    // 1つ前の画面へ戻るのでルーティング上も戻しておく
    routing.backRouting(false);
  };

  useEffect(() => {
    api.getUser().then(ret => {
      !unmounted.current && ret && setUser(ret);
    });

    return () => {
      // unmount
      unmounted.current = true;
    };
  }, []);

  useEffectMounted(() => {
    routing.finishNormalRouting();
  });

  const routingList = sessionStorage.getItem('routing');
  const isFromAccountLinkageInfo =
    routingList?.indexOf('accountlinkageinfo') !== -1;

  // 証券会社
  const securities = securityCompanies.map((item, index) => {
    if (isIosApp() && !item.isIosShow) return <></>;

    const isAvailable =
      item.brokerID === consts.MONOBrokerIds.SBI証券 || isPremium();
    const alignmentAccountLength = userBroker.filter(
      list => list.BrokerID === item.brokerID
    ).length;
    const isAccountAlignment = alignmentAccountLength > 0;

    const register = async (): Promise<void> => {
      if (
        item.brokerID === undefined ||
        isLoading ||
        !item.isRegistrationPossible
      )
        return;

      if (item.brokerID !== consts.MONOBrokerIds.SBI証券 && !isPremium()) {
        setShowPremium(true);
        return;
      }

      setIsLoading(true);
      const res = await api.prepareUserBroker({ brokerID: item.brokerID });

      if (
        res.Success &&
        res.ProvisionalID === null &&
        res.OnetimeToken === null
      ) {
        showAccountRegisteringModal();
      } else if (res.Success && res.OnetimeToken !== null) {
        if (isAvailable && item.brokerID) {
          if (alignmentAccountLength < 10) {
            await doRegister(item.brokerID, res);
          } else {
            showAlignmentAccounLimitModal();
          }
        } else {
          setShowPremium(true);
        }
      } else if (!res.Success && res.Status === '410') {
        showDataLinkWithdrawalAccountModal();
      } else if (
        !res.Success &&
        (res.Status === '400' || res.Status === '404')
      ) {
        showWaitUntilNextUpdateModal();
      }
      setIsLoading(false);
    };

    return (
      <>
        <div className="ui-border-b p-2" key={index}>
          <div
            className={`flex items-center py-2 pl-1 ${
              item.isRegistrationPossible ? 'cursor-pointer' : ''
            }`}
            onClick={() => register()}>
            <div
              className={`text-base font-bold ${
                item.isRegistrationPossible ? '' : 'text-bluegray-400'
              }`}>
              {item.name}
            </div>
            {item.brokerID === consts.MONOBrokerIds.SBI証券 && !isPremium() && (
              <div className="flex items-center mr-auto">
                <span className="mx-2 text-white bg-teal-500 rounded-full text-xs px-4 py-1">
                  無料
                </span>
              </div>
            )}
            {isAccountAlignment &&
            (item.brokerID === consts.MONOBrokerIds.SBI証券 ||
              (isPremium() &&
                item.brokerID === consts.MONOBrokerIds.楽天証券)) ? (
              <div className="ml-auto mr-2 text-xs">
                {alignmentAccountLength}件登録中
              </div>
            ) : (
              <></>
            )}
            {isAvailable ? (
              <>
                {item.isRegistrationPossible ? (
                  <Icon
                    name="chevron-right"
                    className={`text-gray-500 text-2xl ${
                      !isAccountAlignment ? 'ml-auto' : ''
                    }`}
                  />
                ) : (
                  <div className="ml-auto mr-2 text-1.5xs text-bluegray-400">
                    準備中
                  </div>
                )}
              </>
            ) : (
              <>
                {item.brokerID === consts.MONOBrokerIds.楽天証券 ? (
                  <div
                    className={`flex flex-row ml-auto items-center ${
                      !isAccountAlignment ? 'ml-auto' : ''
                    }`}>
                    <Icon name="lock" className="text-lock" />
                    <div className="text-1.5xs text-bluegray-500 ml-1 mr-2">
                      みんかぶプレミアム会員限定
                    </div>
                  </div>
                ) : (
                  <div className="ml-auto mr-2 text-1.5xs text-bluegray-400">
                    準備中
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <DataLinkWithdrawalAccountModal
          hideModal={hideDataLinkWithdrawalAccountModal}
          isShowModal={isShowDataLinkWithdrawalAccountModal}
        />
        <WaitUntilNextUpdateModal
          hideModal={hideWaitUntilNextUpdateModal}
          isShowModal={isShowWaitUntilNextUpdateModal}
          errorCode={'101'}
        />
      </>
    );
  });

  // iDeCo・企業型確定拠出年金
  const definedContributionPlan = definedContributionPlanCompanies.map(
    (item, index) => {
      const isAvailable = isPremium();
      const alignmentAccountLength = userBroker.filter(
        list => list.BrokerID === item.brokerID
      ).length;
      const isAccountAlignment = alignmentAccountLength > 0;

      const register = async (): Promise<void> => {
        if (item.brokerID === undefined || isLoading) return;
        if (!isAvailable) {
          setShowPremium(true);
          return;
        }

        setIsLoading(true);
        const res = await api.prepareUserBroker({ brokerID: item.brokerID });

        if (
          res.Success &&
          res.ProvisionalID === null &&
          res.OnetimeToken === null
        ) {
          showAccountRegisteringModal();
        } else if (res.Success && res.OnetimeToken !== null) {
          if (isAvailable && item.brokerID) {
            if (alignmentAccountLength < 10) {
              await doRegister(item.brokerID, res);
            } else {
              showAlignmentAccounLimitModal();
            }
          } else {
            setShowPremium(true);
          }
        } else if (!res.Success && res.Status === '410') {
        }
        setIsLoading(false);
      };

      return (
        <div className="ui-border-b p-2" key={index}>
          <div
            className={`flex items-center py-2 pl-1 cursor-pointer`}
            onClick={() => register()}>
            <div className="text-base font-bold">{item.name}</div>
            {isAccountAlignment &&
            (isPremium() || item.brokerID === consts.MONOBrokerIds.SBI証券) ? (
              <div className="ml-auto mr-2 text-xs">
                {alignmentAccountLength}件登録中
              </div>
            ) : (
              <></>
            )}
            {isAvailable && item.brokerID === consts.MONOBrokerIds.SBI証券 ? (
              <Icon
                name="chevron-right"
                className={`text-gray-500 text-2xl ${
                  !isAccountAlignment ? 'ml-auto' : ''
                }`}
              />
            ) : isAvailable &&
              !(item.brokerID === consts.MONOBrokerIds.SBI証券) ? (
              <Icon
                name="chevron-right"
                className={`text-gray-500 text-2xl ${
                  !isAccountAlignment ? 'ml-auto' : ''
                }`}
              />
            ) : (
              <div
                className={`flex flex-row ml-auto items-center ${
                  !isAccountAlignment ? 'ml-auto' : ''
                }`}>
                <Icon name="lock" className="text-lock" />
                <div className="text-1.5xs text-bluegray-500 ml-1 mr-2">
                  みんかぶプレミアム会員限定
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }
  );

  const registScroll = () => {
    // スクロール
    const scrollStorage = getScrollStorage();
    const routing = getRoutingStorage();
    const routeList = routing?.routeList;
    const routeItem =
      routeList === undefined ? null : routeList[routeList.length - 1];
    const height = window.scrollY;

    if (routeItem !== null && routeItem !== undefined && height !== 0) {
      if (scrollStorage === null) {
        const list = [{ path: routeItem.path, height }] as Scroll[];
        updateScrollStorage(list);
      } else {
        const list = [...scrollStorage, { path: routeItem.path, height }];
        updateScrollStorage(list);
      }
    }
  };

  useScroll();

  return (
    <>
      {!isIosApp() && (
        <>
          {!isPremium() && !isApp && (
            <>
              <a
                href={`${env.premiumURL}?service=${
                  env.serviceName
                }&utm_source=tw_minkabu&utm_medium=securityaccountslink&utm_campaign=premium&callback_url=${encodeURI(
                  `${env.appURL}/auth?callbackPath=/securityaccountslink`
                )}`}
                target="_self"
                className="flex items-center justify-center">
                <img
                  src="/static/images/securityaccountslink/account_premium_banner.svg"
                  className="w-full"
                  alt=""
                />
              </a>
            </>
          )}
        </>
      )}
      <div className="mt-4 mx-2">
        <div className="pb-2 px-2 ui-border-b">
          <div className="flex items-center">
            {/* 証券会社 */}
            <div className="flex-grow text-lg font-bold">証券会社</div>
            {!isIosApp() && (
              <div className="flex text-bluegray-500 text-sm">
                <a
                  href={SUPPORT_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm">
                  連携可能な情報
                </a>
                <Icon
                  name="external-link"
                  className="text-base mx-1 text-gray-500"
                />
              </div>
            )}
          </div>
        </div>
        <div className="bg-white mb-2 border border-gray-300 overflow-hidden text-gray-800 rounded-lg">
          {securities}
        </div>

        {/* iDeCo・企業型確定拠出年金 */}
        {!isIosApp() && (
          <>
            <div className="flex items-center pt-6 pb-2 px-2">
              <div className="flex-grow text-lg font-bold">
                iDeCo・企業型確定拠出年金
              </div>
              <div className="flex text-bluegray-500 text-sm">
                <a
                  href={SUPPORT_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm">
                  対応金融機関
                </a>
                <Icon
                  name="external-link"
                  className="text-base mx-1 text-gray-500"
                />
              </div>
            </div>
            <div className="bg-white mb-2 border border-gray-300 overflow-hidden text-gray-800 rounded-lg">
              {definedContributionPlan}
            </div>
          </>
        )}

        {!isIosApp() && (
          <>
            {/* その他の証券/銀行など */}
            {!user ? null : !user.LinkedMoneytree ||
              !user.AuthenticatedMoneytree ? (
              <>
                <div className="flex items-center pt-6 pb-2 px-2">
                  <div className="flex-grow text-lg font-bold">
                    その他の証券/銀行など
                  </div>
                  <div className="flex text-bluegray-500 text-sm">
                    <a
                      href="https://institutions.moneytree.jp/index.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm">
                      対応金融機関
                    </a>
                    <Icon
                      name="external-link"
                      className="text-base mx-1 text-gray-500"
                    />
                  </div>
                </div>
                <div className="bg-white mb-2 border border-gray-300 overflow-hiddentext-gray-800 pr-2 pl-3 py-2 rounded-lg cursor-pointer">
                  <div
                    onClick={(): void => {
                      if (isLoading) return;
                      if (isPremium()) {
                        handleMoneytreeLink();
                        window.location.href = `${moneytreeAuthorizeURL}`;
                      } else {
                        setShowPremium(true);
                      }
                    }}>
                    <div className="flex items-center">
                      <div className="flex flex-col">
                        <div className="text-base font-bold">
                          Moneytree IDを連携する
                        </div>
                        <div className="text-xs">
                          その他証券や銀行口座と連携できる
                        </div>
                        {!isPremium() && (
                          <div className="flex flex-row items-center mt-1">
                            <Icon name="lock" className="text-lock" />
                            <div className="text-1.5xs text-bluegray-500 ml-1">
                              みんかぶプレミアム会員限定
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="ml-auto">
                        <img
                          className="mr-2"
                          src="/static/images/securityaccountslink/moneytree.svg"
                          width="56"
                          height="63"
                          alt=""
                        />
                      </div>
                      {isPremium() && (
                        <div className="ml-4">
                          <Icon
                            name="chevron-right"
                            className="text-gray-500 text-2xl"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-grow text-bluegray-500">
                  <div
                    className="flex text-sm items-center cursor-pointer"
                    onClick={showInformationFromMoneytreeModal}>
                    Moneytreeから取得できる情報
                    <Icon name="info" className="text-base ml-1" />
                  </div>
                </div>
              </>
            ) : (
              <></>
            )}
          </>
        )}

        <div className="text-lg font-bold pt-6 px-2">資産を直接入力</div>
        <div className="bg-white mb-8 border border-gray-300 overflow-hidden text-gray-800 pr-2 pl-3 py-2 rounded-lg">
          <Link
            to={
              !isLoading
                ? linkToOptionalAssetRegistration(
                    isFromAccountLinkageInfo ? 'accountLinkedInfo' : 'top'
                  )
                : ''
            }
            onClick={(): void => registScroll()}>
            <div className="flex flex-row items-center">
              <div className="flex flex-col">
                <div className="text-base font-bold">直接入力</div>
                <div className="text-xs">
                  不動産、タンス預金、金/プラチナなど
                </div>
              </div>
              <div className="ml-auto">
                <img
                  className=""
                  src="/static/images/securityaccountslink/manual.svg"
                  width="56"
                  height="63"
                  alt=""
                />
              </div>
              <div className="">
                <Icon name="chevron-right" className="text-gray-500 text-2xl" />
              </div>
            </div>
          </Link>
        </div>
      </div>
      <div className="bg-white p-4 lg:mx-2">
        <div className="text-sm text-teal-500 text-center leading-5 font-bold mb-4">
          口座連携でお困りですか？
        </div>
        <div className="flex items-start ">
          <img
            className="mr-4"
            src="/static/images/securityaccountslink/renkei_error_img.svg"
            width="80"
            height="80"
            alt=""
          />
          <div className="flex-1 text-sm text-bluegray-700 mb-2">
            <div className="font-bold leading-5 mb-1">
              口座の連携時は下記にお気をつけ下さい
            </div>
            <div className="leading-6">
              (1)口座の連携中は画面を閉じずにお待ちください。
              <br />
              (2)同一証券の同一口座は１口座までしか登録できません。
            </div>
          </div>
        </div>
        <a
          id="accountLinkageInfo_accountRegistrationNotes"
          href="https://support.minkabu.jp/hc/ja/articles/900004587786-%E5%8F%A3%E5%BA%A7%E7%99%BB%E9%8C%B2%E6%B3%A8%E6%84%8F%E4%BA%8B%E9%A0%85"
          target="_blank"
          rel="noopener noreferrer">
          <div className="text-right text-sm text-blue-600 leading-5 mb-3 cursor-pointer">
            口座登録注意事項を確認する
          </div>
        </a>
        <div className="p-4 rounded-lg bg-bluegray-60 text-bluegray-500 text-sm leading-5">
          ※上記以外のケースで連携が正常に完了できない場合は、翌日以降に再度連携してください。
        </div>
      </div>

      {isLoading && (
        <div className="absolute top-0 left-0 h-full w-full bg-gray-800 opacity-75 flex justify-center items-center z-20">
          <Loading
            loading={true}
            circularLoading={true}
            style={{
              backgroundColor: 'transparent',
            }}
          />
        </div>
      )}

      {shownUnlinkConfirmDialog && (
        <OKCanelModal onOK={doUnlinkOK} onCancel={doUnlinkCancel}>
          <div className="p-4">Moneytreeとの連携を解除します。</div>
        </OKCanelModal>
      )}
      {showPremium && (
        <PremiumModal layoutPattern={1} onClose={onClosePremium} />
      )}
      <InformationFromMoneytreeModal
        hideModal={hideInformationFromMoneytreeModal}
        isShowModal={isShowInformationFromMoneytreeModal}
      />
      <AlignmentAccounLimitModal
        hideModal={hideAlignmentAccounLimitModal}
        isShowModal={isShowAlignmentAccounLimitModal}
      />
      <AccountRegisteringModal
        hideModal={hideAccountRegisteringModal}
        isShowModal={isShowAccountRegisteringModal}
      />
      <Element />
    </>
  );
};

const SecurityAccountsLink: React.FC = () => {
  const { data } = useQuery('assetStuffingAssets', () => getUserBrokerAPI(), {
    refetchOnWindowFocus: false,
  });

  if (data === undefined || data === null)
    return <Loading loading style={{ marginTop: '3rem' }} />;

  const userBroker = data.filter(
    (data: api.GetUserBrokerResultItem) =>
      data.AggregateSource === consts.AggregateSourceTypes.MONO
  );

  return <SecurityAccountsLinkInternal {...{ userBroker }} />;
};

const getUserBrokerAPI = async () => {
  const res_ = await api.getUserBroker();
  return res_.Items;
};

export default SecurityAccountsLink;
